import React from 'react';
import { Button, Dropdown, Tooltip, Typography } from '../../../components';

const { Text } = Typography;
import {
    isApplianceAvailable,
    compareVersions,
    getSessionVar,
    isApplianceUpgradeableToVersion,
} from '../../../lib/helpers';
import {
    getCloudLogo,
    getApplianceReadiness,
    getMigrationApplianceURL,
    getApplianceDeploymentState,
    formatTags,
    canApplianceUpgrade,
    canTroubleshoot,
    canRedeploy,
    APPLIANCE_READINESS_STATE_INFO,
} from '../utils';
import { CONFIG } from '../../../constants';

import {
    ArrowUpIcon,
    ExclamationIcon,
    EditIcon,
    ApplianceRedeployIcon,
    WrenchIcon,
    TrashIcon,
} from '../../../components/icons/';

import { MoreOutlined } from '@ant-design/icons';

export const createTableColumns = ({
    handleOpenDeleteModal,
    handleUpgradeAppliance,
    handleOpenTroubleshootingModal,
    handleEditCloudAccount,
    handleRedeployAppliance,
}) => {
    // Action menu for each row
    const getActionMenu = (record) => {
        const items = [
            {
                key: 'upgrade',
                icon: <ArrowUpIcon grey />,
                label: 'Upgrade Migration Appliance',
                disabled: !canApplianceUpgrade(record),
                onClick: () => handleUpgradeAppliance(record),
            },
            {
                key: 'edit',
                icon: <EditIcon grey />,
                label: 'Edit Cloud Account',
                onClick: () => handleEditCloudAccount(record.id),
            },
            {
                key: 'redeploy',
                icon: <ApplianceRedeployIcon grey />,
                label: 'Redeploy Migration Appliance',
                disabled: !canRedeploy(record),
                onClick: () => handleRedeployAppliance(record.id),
            },
            {
                key: 'troubleshooting',
                icon: <WrenchIcon grey />,
                label: 'Network Troubleshooting',
                disabled: !canTroubleshoot(record),
                onClick: () => handleOpenTroubleshootingModal(record.id, record.name),
            },
            {
                key: 'delete',
                icon: <TrashIcon error />,
                label: 'Remove Cloud Target Account',
                danger: true,
                disabled: !isApplianceAvailable(record),
                onClick: () => handleOpenDeleteModal(record.id, record.name),
            },
        ];

        return { items };
    };

    return [
        {
            title: 'Name',
            dataIndex: 'name',
            width: 245,
            key: 'name',
            ellipsis: {
                showTitle: false, // Disable default title, we'll use custom tooltip
            },
            render: (_, record) => {
                const CloudLogo = getCloudLogo(record.type);
                const isDevelopmentMode = WOWO.getConfigurationValue('mode_env', localStorage) === 'development';
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CloudLogo className="cloud-logo" />
                        <Text ellipsis={{ tooltip: true }} copyable={isDevelopmentMode} style={{ flex: 4, width: 180 }}>
                            {record.name}
                        </Text>
                    </div>
                );
            },
        },
        {
            title: 'URL',
            dataIndex: 'url',
            render: (_, row) => getMigrationApplianceURL(row) || '-',
            key: 'url',
            width: 400,
            ellipsis: true,
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            className: 'tags-column cursor-pointer',
            key: 'tags',
            width: 168,
            ellipsis: true,
            render: (_, row) => {
                return (
                    <Tooltip title="Click to Add/Edit tags">
                        <span>{formatTags(row.tags)}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Deployment',
            width: 200,
            render: (_, row) => getApplianceDeploymentState(row.appliance),
            key: 'applianceDeployment',
        },
        {
            title: 'Readiness',
            width: 200,
            className: 'appliance-readiness',
            render: (_, row) => {
                return getApplianceReadiness(row, APPLIANCE_READINESS_STATE_INFO) ?? 'N/A';
            },
            key: 'applianceReadiness',
        },
        {
            title: 'Version',
            render: (_, row) => {
                const applianceVersionNum = row.appliance?.appliance_version_num;
                if (!applianceVersionNum) return '-';

                const applianceLatestVersion = getSessionVar('applianceLatestVersion');
                const showWarning = compareVersions(applianceVersionNum, applianceLatestVersion) < 0;
                const versionTooOld = compareVersions(applianceVersionNum, CONFIG.CUTOFF_VERSION_FOR_UPGRADE_AVAIL) < 0;
                const versionUnUpgradeable = !isApplianceUpgradeableToVersion(
                    applianceVersionNum,
                    applianceLatestVersion,
                );

                return (
                    <span className="version-cell icon-center">
                        <span className="secondary-text">{applianceVersionNum}</span>
                        {applianceVersionNum && (versionTooOld || versionUnUpgradeable) && (
                            <Tooltip
                                title={
                                    <span>
                                        A newer Migration Appliance version is available. Please{' '}
                                        <a
                                            target="_blank"
                                            href="http://docs.rivermeadow.com/redeploy-cloudappliance"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            redeploy
                                        </a>{' '}
                                        for the latest functionality.
                                    </span>
                                }
                                placement="right"
                            >
                                <ExclamationIcon onClick={(e) => e.stopPropagation()} />
                            </Tooltip>
                        )}
                        {applianceVersionNum && showWarning && !(versionTooOld || versionUnUpgradeable) && (
                            <Tooltip
                                title={
                                    <span>
                                        A newer Migration Appliance version is available. Please upgrade for the latest
                                        functionality. View{' '}
                                        <a
                                            target="_blank"
                                            href="http://docs.rivermeadow.com/upgrade-cloud-appliance"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Upgrade Migration Appliance
                                        </a>
                                        .
                                    </span>
                                }
                                placement="right"
                            >
                                <ArrowUpIcon onClick={(e) => e.stopPropagation()} />
                            </Tooltip>
                        )}
                    </span>
                );
            },
            key: 'version',
            width: 155,
        },
        {
            title: '',
            key: 'actions',
            fixed: 'right',
            width: 75,
            align: 'center',
            render: (_, record) => {
                const handleMenuClick = (e) => {
                    e.domEvent.stopPropagation();
                };
                return (
                    <Dropdown
                        menu={{
                            ...getActionMenu(record),
                            onClick: handleMenuClick,
                        }}
                        trigger={['click']}
                    >
                        <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                    </Dropdown>
                );
            },
        },
    ];
};
